import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import homepageImage10 from "../../images/homePageImages/homepageimage10.png";
import homepageImage8 from "../../images/homePageImages/homepageimage8.png";
import homepageImage11 from "../../images/homePageImages/homepageimage11.png";

import "./businessLanding.css";
import PhoneCarousel from "../common/phoneCarousel/PhoneCarousel";
import useReveal from "../common/useReveal";

/* Several copy strings open with an emoji. Pull it out so it can be rendered as
   a proper icon tile instead of sitting cramped against the text. */
const LEADING_EMOJI = /^\s*(\p{Extended_Pictographic}\uFE0F?(?:\u200D\p{Extended_Pictographic}\uFE0F?)*)\s*/u;

const splitLeadingEmoji = (text) => {
	const match = typeof text === "string" ? text.match(LEADING_EMOJI) : null;
	return match ? { icon: match[1], label: text.slice(match[0].length) } : { icon: null, label: text };
};

const BusinessLanding = () => {
	const { language } = useContext(CombinedContext);
	const navigate = useNavigate();
	const rootRef = useReveal();

	const t = translations[language].businessLanding;
	const problemHighlight = splitLeadingEmoji(t.problem.highlight);
	const dykHighlight = splitLeadingEmoji(t.didYouKnow.highlight);
	const problemDescription = splitLeadingEmoji(t.problem.description);

	return (
		<div className="sf-bl-root" ref={rootRef}>
			{/* HERO */}
			<section className="sf-bl-hero">
				<div className="sf-bl-hero-grid" aria-hidden="true"></div>
				<div className="sf-bl-hero-inner">
					<div className="sf-bl-hero-left">
						<div className="sf-bl-hero-badge">
							<span className="sf-bl-hero-badge-dot"></span>
							{t.misc.badge}
						</div>

						<h1>{t.hero.title}</h1>

						<p className="sf-bl-hero-sub">{t.hero.subtitle}</p>

						<div className="sf-bl-hero-buttons">
							<button className="sf-bl-btn-primary" onClick={() => navigate("/listyourshop")}>
								{t.hero.primaryCta}
								<ArrowRight size={17} className="sf-bl-btn-arrow" />
							</button>
							<button className="sf-bl-btn-ghost">{t.hero.storeText}</button>
						</div>

						<p className="sf-bl-hero-note">
							<em>
								<strong>{t.hero.noteStrong}</strong>
								{t.hero.noteRest}
							</em>
						</p>
					</div>

					<div className="sf-bl-hero-img-wrap">
						<img src={homepageImage10} alt="" className="sf-bl-hero-img" />
					</div>
				</div>
			</section>

			{/* PAGE BODY */}
			<div className="sf-bl-page">
				{/* PROBLEM */}
				<section className="sf-bl-problem">
					<div className="sf-bl-section-head sf-bl-center" data-reveal>
						<span className="sf-bl-section-label">{t.misc.labelChallenge}</span>
						<h2>{t.problem.title}</h2>
					</div>

					<div className="sf-bl-problem-grid">
						<div className="sf-bl-pcard sf-bl-pcard-red" data-reveal>
							<h3>{t.problem.subtitle}</h3>
							<ul>
								{t.problem.items.map((item, i) => {
									const { icon, label } = splitLeadingEmoji(item);
									return (
										<li key={i}>
											{icon ? (
												<span className="sf-bl-li-icon sf-bl-li-icon-red">{icon}</span>
											) : (
												<div className="sf-bl-dot sf-bl-dot-red"></div>
											)}
											<span>{label}</span>
										</li>
									);
								})}
							</ul>
						</div>

						<div className="sf-bl-pcard sf-bl-pcard-blue" data-reveal>
							<h3>
								{problemDescription.icon && (
									<span className="sf-bl-pcard-lead" aria-hidden="true">{problemDescription.icon}</span>
								)}
								{problemDescription.label}
							</h3>
							<ul>
								{t.problem.itemspart2.map((item, i) => {
									const { icon, label } = splitLeadingEmoji(item);
									return (
										<li key={i}>
											{icon ? (
												<span className="sf-bl-li-icon sf-bl-li-icon-blue">{icon}</span>
											) : (
												<div className="sf-bl-dot sf-bl-dot-blue"></div>
											)}
											<span>{label}</span>
										</li>
									);
								})}
							</ul>
							<div className="sf-bl-problem-highlight">
								<span className="sf-bl-hl-icon">{problemHighlight.icon || "⚠️"}</span>
								<p>{problemHighlight.label}</p>
							</div>
						</div>
					</div>
				</section>

				{/* DID YOU KNOW */}
				<section className="sf-bl-didyouknow">
					<div className="sf-bl-dyk-inner">
						<div className="sf-bl-dyk-left" data-reveal>
							<span className="sf-bl-section-label">{t.misc.labelResearch}</span>
							<h2>{t.didYouKnow.title}</h2>
							<ul className="sf-bl-dyk-list">
								{t.didYouKnow.items.map((item, i) => (
									<li key={i}>
										<span className="sf-bl-dyk-bullet">{i + 1}</span>
										{item}
									</li>
								))}
							</ul>
							<div className="sf-bl-didyouknow-highlight">
								<span className="sf-bl-dyk-hl-icon">{dykHighlight.icon || "✅"}</span>
								<p>{dykHighlight.label}</p>
							</div>
						</div>
						<div className="sf-bl-dyk-img-wrap" data-reveal>
							<img src={homepageImage8} alt="" className="sf-bl-dyk-img" />
						</div>
					</div>
				</section>

				{/* SOLUTION */}
				<section className="sf-bl-solution">
					<div className="sf-bl-section-head sf-bl-center" data-reveal>
						<span className="sf-bl-section-label">{t.misc.labelHowItWorks}</span>
						<h2>{t.solution.title}</h2>
					</div>

					<div className="sf-bl-solution-grid">
						<ul className="sf-bl-solution-steps">
							{t.solution.points.map((p, i) => (
								<li key={i} className="sf-bl-step-item" data-reveal>
									<div className="sf-bl-step-num">{i + 1}</div>
									<div className="sf-bl-step-content">
										<strong>{p.title}</strong>
										<p>{p.desc}</p>
									</div>
								</li>
							))}
						</ul>

						<div className="sf-bl-solution-phone" data-reveal>
							<div className="sf-bl-sol-glow"></div>
							<PhoneCarousel />
						</div>
					</div>
				</section>

				{/* PRICING */}
				<section className="sf-bl-pricing">
					<div className="sf-bl-section-head sf-bl-center" data-reveal>
						<span className="sf-bl-section-label">{t.misc.labelPricing}</span>
						<h2>{t.pricing.title}</h2>
					</div>

					<div className="sf-bl-pricing-grid">
						<div className="sf-bl-pricing-card sf-bl-pricing-card-dark" data-reveal>
							<h3>{t.pricing.planTitle}</h3>
							<div className="sf-bl-price-row">
								<span className="sf-bl-price-big">{t.pricing.priceNote}</span>
							</div>
							<ul>
								{t.pricing.planItems.map((p, i) => {
									const { icon, label } = splitLeadingEmoji(p);
									return (
										<li key={i}>
											<span className="sf-bl-ck">{icon || "✓"}</span>
											{label}
										</li>
									);
								})}
							</ul>
							{/* <p className="sf-bl-cap-note">{t.pricing.capNote}</p> */}
							<p className="sf-bl-pricing-note">{t.pricing.note}</p>
						</div>

						<div className="sf-bl-pricing-card-other" data-reveal>
							<h3>{t.pricing.autoTitle}</h3>
							<div className="sf-bl-auto-list">
								{t.pricing.autoItems.map((a, i) => (
									<div key={i} className="sf-bl-auto-li">
										<div className="sf-bl-auto-ic">{i === 0 ? "💳" : i === 1 ? "🔄" : "✓"}</div>
										{a}
									</div>
								))}
							</div>
						</div>
					</div>

					<div className="sf-bl-pricing-features" data-reveal>
						<h3>{t.pricing.featuresTitle}</h3>
						<div className="sf-bl-pf-grid">
							{t.pricing.features.map((f, i) => (
								<div key={i} className="sf-bl-pf-item">
									<span className="sf-bl-ck">✓</span>
									{f}
								</div>
							))}
						</div>
					</div>
				</section>

				{/* REFERRAL */}
				<section className="sf-bl-referral">
					<div className="sf-bl-section-head sf-bl-center" data-reveal>
						<span className="sf-bl-section-label">{t.misc.labelReferral}</span>
						<h2>{t.referral.title}</h2>
						<p className="sf-bl-referral-subtitle">{t.referral.subtitle}</p>
					</div>

					<div className="sf-bl-referral-grid">
						<div className="sf-bl-ref-img-wrap" data-reveal>
							<img src={homepageImage11} alt="" className="sf-bl-ref-img" />
						</div>
						<div className="sf-bl-ref-steps" data-reveal>
							<span className="sf-bl-section-label">{t.referral.sectionLabel}</span>
							{t.referral.cards.map((c, i) => (
								<div key={i} className={`sf-bl-ref-card sf-bl-ref-card-${c.tone}`}>
									<div className="sf-bl-ref-card-head">
										<div className="sf-bl-ref-card-icon" aria-hidden="true">{c.icon}</div>
										<h3>{c.title}</h3>
									</div>
									<p className="sf-bl-ref-card-lead">{c.desc}</p>
									{/* {c.bullets && (
										<ul className="sf-bl-ref-card-list">
											{c.bullets.map((item, j) => (
												<li key={j}>
													<CheckCircle size={16} className="sf-bl-ref-check" />
													<span>{item}</span>
												</li>
											))}
										</ul>
									)} */}
								</div>
							))}
						</div>
					</div>

					<div className="sf-bl-referral-cta" data-reveal>
						{/* <p className="sf-bl-referral-micro">{t.referral.microCopy}</p> */}
						<button className="sf-bl-referral-more-link" onClick={() => navigate("/referral-program")}>
							{t.referral.moreDetailsLink}
							<ArrowRight size={16} />
						</button>
					</div>
				</section>

				{/* TESTIMONIALS */}
				<section className="sf-bl-testimonials">
					<div className="sf-bl-section-head sf-bl-center" data-reveal>
						<span className="sf-bl-section-label">{t.misc.labelTestimonials}</span>
						<h2>{t.testimonials.title}</h2>
					</div>

					<div className="sf-bl-testimonial-grid">
						{t.testimonials.items.map((item, i) => (
							<div className="sf-bl-testimonial-card" key={i} data-reveal>
								<span className="sf-bl-testimonial-mark" aria-hidden="true">&ldquo;</span>
								<p className="sf-bl-testimonial-quote">“{item.quote}”</p>
								<div className="sf-bl-testimonial-author">
									<div className="sf-bl-testimonial-avatar" aria-hidden="true">
										{item.name.charAt(0)}
									</div>
									<div>
										<strong>{item.name}</strong>
										<span>{item.role}, {item.location}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* FINAL CTA */}
				<section className="sf-bl-final" data-reveal>
					<p>{t.final.text}</p>
					<button className="sf-bl-btn-primary" onClick={() => navigate("/listyourshop")}>
						{t.final.button}
						<ArrowRight size={18} className="sf-bl-btn-arrow" />
					</button>
				</section>
			</div>
		</div>
	);
};

export default BusinessLanding;
